import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { formatInteger } from '../common/formatHelper';
import { AsyncLoadStatus } from '../common/types';
import * as Icon from '../components/icons';
import { Stat } from '../components/stats/Stat';
import {
  routingPolicy,
  routingPolicyCheckResultByRule,
  routingPolicyCheckResultByRuleLoadStatus,
  routingPolicyRule,
  routingPolicySelectedIdentity,
} from '../simulation/store/routingPolicy/routingPolicy.state';
import { simulationLayoutSummary } from '../simulation/store/simulation.layout.state';
import { simulationIsEditable } from '../simulation/store/simulation.state';
import { sidebarStateByType } from '../store/sidebar.state';
import useCheckRoutingPolicyRule from './useCheckRoutingPolicyRule';

export type RoutingPolicyRuleStatsProps = {
  ruleId: string;
  threshold?: number;
};

const RoutingPolicyRuleStats: React.FC<RoutingPolicyRuleStatsProps> = props => {
  const { ruleId } = props;
  const { t } = useTranslation('simulation');
  const policy = useRecoilValue(routingPolicy);
  const layout = useRecoilValue(simulationLayoutSummary);
  const [selectedIdentity, setSelectedIdentity] = useRecoilState(
    routingPolicySelectedIdentity,
  );
  const rule = useRecoilValue(routingPolicyRule(props.ruleId));
  const [callCheck, cancelCheck] = useCheckRoutingPolicyRule();
  const checkResult = useRecoilValue(routingPolicyCheckResultByRule(ruleId));
  const checkResultStatus = useRecoilValue(
    routingPolicyCheckResultByRuleLoadStatus(ruleId),
  );
  const [policyEditPanelState, setPolicyEditPanelState] = useRecoilState(
    sidebarStateByType('sidebar-routing-policy-editor'),
  );
  const canUpdate = useRecoilValue(simulationIsEditable);

  useEffect(() => {
    cancelCheck();
    callCheck({
      ruleId,
      featureRules: rule?.featureRules,
      threshold: policy?.threshold,
      portalRules: rule?.portalRules,
    });

    return () => {
      cancelCheck();
    };
  }, [rule, props?.threshold]);

  const isActive = selectedIdentity?.ruleId === ruleId;

  function toggleSelect() {
    const shouldSelect = !isActive;
    const newIdentity = shouldSelect
      ? {
          ruleId: ruleId,
          isRoutingAgentSelected: false,
          isRoutingConfigurationSelected: canUpdate,
        }
      : null;

    setSelectedIdentity(newIdentity);
    if (canUpdate) {
      setPolicyEditPanelState({
        ...policyEditPanelState,
        isCollapsed: !policyEditPanelState.isPinned
          ? !shouldSelect
          : policyEditPanelState.isCollapsed,
        isHidden: !shouldSelect,
      });
    }
  }

  const isCheckLoading =
    checkResultStatus === AsyncLoadStatus.Loading ||
    checkResultStatus === AsyncLoadStatus.None;

  const totalCount = layout.locationCount;
  const invalidCount = checkResult;
  const hasIssue = invalidCount > 0;

  return (
    <Stat
      className={classNames('px-1 py-1 xl:px-2')}
      hasIssue={hasIssue}
      isCheckLoading={isCheckLoading}
      title={isCheckLoading ? t`Validating Routing` : t`Inaccessible locations`}
      isSectionable
      value={hasIssue ? formatInteger(invalidCount) : t`None`}
      valueTotal={hasIssue ? formatInteger(totalCount) : null}
      icon={hasIssue ? Icon.TriangleInfo : Icon.CircleOk}
      inPanelMode
      isActionable
      // inFilterStat
      isSelected={isActive}
      onClick={toggleSelect}
      transparent
    />
  );
};

export default RoutingPolicyRuleStats;
